/**
 * @fileoverview Firm Contacts directory — main view.
 *
 * Org-scoped curated CRM list. Banks, regulators, professionals, and a
 * row per org member (auto-created from `orgMemberships`). Every org
 * member can browse and add (`canAdd`); editing / archiving / importing is
 * gated to owners/admins via `canEdit` (limited-access banner + disabled
 * affordances) and enforced server-side.
 *
 * Job CRM contacts auto-link to a matching firm contact during reconcile
 * (ABN > ACN > normalised name) so editing here propagates to every
 * job that references this entity.
 */

import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { extractErrorMessage } from "@/utils/errors";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Archive,
  ChevronDown,
  Download,
  Loader2,
  Lock,
  Pencil,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/ui/primitives/input";
import { Button } from "@/ui/primitives/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import { FacetedFilter } from "@/ui/composed/filters/faceted-filter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  CONTACT_TYPE_META,
  CONTACT_TYPE_ORDER,
  FIRM_CATEGORY_META,
  FIRM_CATEGORY_ORDER,
  firmContactState,
  resolveFirmCategoryDisplay,
  type ContactType,
  type FirmContactCustomCategory,
  type FirmContactRow,
} from "./types";
import { AU_STATE_VALUES } from "@/utils/format/auStates";
import {
  FirmContactsDataTable,
  type FirmContactGroupBy,
} from "./FirmContactsDataTable";
import { FirmContactDetailPanel } from "./FirmContactDetailPanel";
import { FirmContactImportDialog } from "./FirmContactImportDialog";
import { NewFirmContactDialog } from "./NewFirmContactDialog";
import { BulkEditFirmContactsDialog } from "./BulkEditFirmContactsDialog";
import {
  buildFirmContactsCustomExportDatasets,
  exportFirmContactsTableToCsv,
  type FirmContactsCsvScope,
} from "./exportFirmContacts";
import { CustomExportDialog } from "@/components/customExport";

const BUILTIN_CATEGORY_FILTER_OPTIONS = FIRM_CATEGORY_ORDER.map((c) => ({
  value: c as string,
  label: FIRM_CATEGORY_META[c].label,
}));

const TYPE_FILTER_OPTIONS = CONTACT_TYPE_ORDER.map((t) => ({
  value: t,
  label: CONTACT_TYPE_META[t].label,
}));

interface FirmContactsViewProps {
  orgId: Id<"organizations">;
  /** Owners/admins can edit / archive / import; members get a read-only view. */
  canEdit: boolean;
  /** Any org member can add a firm contact, even without edit access. */
  canAdd: boolean;
}

/**
 * Notice shown to org members who lack edit access, mirroring the precedent
 * library's banner. Members may still add contacts — only editing / archiving
 * is admin-gated.
 */
function ReadOnlyBanner() {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
      <Lock className="mt-0.5 size-4 shrink-0" />
      <div className="text-sm">
        <p className="font-medium">Limited access</p>
        <p className="mt-0.5 text-xs opacity-80">
          You can add firm contacts, but editing or archiving existing ones
          needs owner or admin access. Ask an admin in your organisation to make
          those changes.
        </p>
      </div>
    </div>
  );
}

export function FirmContactsView({
  orgId,
  canEdit,
  canAdd,
}: FirmContactsViewProps) {
  const [search, setSearch] = useState("");
  /** Selected category keys — built-in categories or `custom:<id>` (MOR-914). */
  const [activeCategories, setActiveCategories] = useState<Set<string>>(
    new Set(),
  );
  const [activeTypes, setActiveTypes] = useState<Set<ContactType>>(new Set());
  /** Address-state filter (MOR-913). `"__none__"` matches contacts with no state. */
  const [activeStates, setActiveStates] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy] = useState<FirmContactGroupBy>("none");
  const [showArchived, setShowArchived] = useState(false);
  const [selectedFirmContactId, setSelectedFirmContactId] =
    useState<Id<"firmContacts"> | null>(null);

  // Bulk selection (MOR-915) — mirrors the precedent library's flow.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  // Custom export tool (MOR-1048): compose / save / export over all rows.
  const [customExportOpen, setCustomExportOpen] = useState(false);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
  const [bulkArchiving, setBulkArchiving] = useState(false);

  const bulkArchive = useConvexMutation(
    api.contacts.mutations.bulkArchiveFirmContacts,
  );

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setManySelected = useCallback((ids: string[], selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (selected) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handleBulkArchive = async () => {
    setBulkArchiving(true);
    try {
      const { archived } = await bulkArchive({
        orgId,
        firmContactIds: Array.from(selectedIds) as Array<Id<"firmContacts">>,
      });
      toast.success(
        `Archived ${archived} contact${archived === 1 ? "" : "s"}.`,
      );
      setBulkArchiveOpen(false);
      clearSelection();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not archive contacts."));
    } finally {
      setBulkArchiving(false);
    }
  };

  const { data, isLoading } = useQuery({
    ...convexQuery(api.contacts.queries.listFirmContacts, {
      orgId,
      showArchived,
    }),
  });

  const { data: customCategoriesData } = useQuery({
    ...convexQuery(api.contacts.customCategories.listFirmContactCategories, {
      orgId,
    }),
  });
  const customCategories = useMemo<Array<FirmContactCustomCategory>>(
    () => customCategoriesData ?? [],
    [customCategoriesData],
  );
  const customCategoriesById = useMemo(
    () => new Map(customCategories.map((c) => [String(c._id), c])),
    [customCategories],
  );

  const allRows = useMemo<Array<FirmContactRow>>(() => data ?? [], [data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allRows.filter((row) => {
      if (term) {
        const haystack = [
          row.displayName,
          row.linkedUser?.email ?? "",
          row.email ?? "",
          row.phone ?? "",
          row.abn ?? "",
          row.acn ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (activeCategories.size > 0) {
        const key = resolveFirmCategoryDisplay(row, customCategoriesById).key;
        if (!activeCategories.has(key)) return false;
      }
      if (activeTypes.size > 0 && !activeTypes.has(row.contactType)) {
        return false;
      }
      if (activeStates.size > 0) {
        const state = firmContactState(row) ?? "__none__";
        if (!activeStates.has(state)) return false;
      }
      return true;
    });
  }, [
    allRows,
    search,
    activeCategories,
    activeTypes,
    activeStates,
    customCategoriesById,
  ]);

  const categoryCountsMap = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of allRows) {
      const key = resolveFirmCategoryDisplay(row, customCategoriesById).key;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [allRows, customCategoriesById]);

  const categoryFilterOptions = useMemo(
    () => [
      ...BUILTIN_CATEGORY_FILTER_OPTIONS,
      ...customCategories.map((c) => ({
        value: `custom:${c._id}`,
        label: c.name,
      })),
    ],
    [customCategories],
  );

  const typeCountsMap = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of allRows) {
      counts.set(row.contactType, (counts.get(row.contactType) ?? 0) + 1);
    }
    return counts;
  }, [allRows]);

  const stateCountsMap = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of allRows) {
      const state = firmContactState(row) ?? "__none__";
      counts.set(state, (counts.get(state) ?? 0) + 1);
    }
    return counts;
  }, [allRows]);

  /**
   * State filter options: AU states first (canonical order), then any other
   * values present in the data, then a "No state" bucket when some contacts
   * have no address state. Only states that actually occur are offered.
   */
  const stateFilterOptions = useMemo(() => {
    const present = new Set(
      allRows.map((row) => firmContactState(row)).filter(Boolean) as string[],
    );
    const known = AU_STATE_VALUES.filter((s) => present.has(s));
    const other = [...present]
      .filter((s) => !AU_STATE_VALUES.includes(s))
      .sort();
    const options = [...known, ...other].map((s) => ({ value: s, label: s }));
    if (stateCountsMap.has("__none__")) {
      options.push({ value: "__none__", label: "No state" });
    }
    return options;
  }, [allRows, stateCountsMap]);

  const archivedCount = allRows.filter((c) => c.status === "archived").length;

  const isFiltered =
    activeCategories.size > 0 ||
    activeTypes.size > 0 ||
    activeStates.size > 0 ||
    search.trim().length > 0;

  const resetFilters = () => {
    setSearch("");
    setActiveCategories(new Set());
    setActiveTypes(new Set());
    setActiveStates(new Set());
  };

  const handleTableExport = (scope: FirmContactsCsvScope) => {
    const rowsToExport = scope === "current" ? filteredRows : allRows;
    if (rowsToExport.length === 0) {
      toast.error("No contacts to export.");
      return;
    }
    try {
      exportFirmContactsTableToCsv(rowsToExport, scope);
      toast.success(
        `Exported ${rowsToExport.length} contact${rowsToExport.length === 1 ? "" : "s"}.`,
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to export contacts.",
      );
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              Firm Contacts
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage firm-level contacts that are applicable across various jobs
              (e.g. regulators, banks, insurers, etc) and users in your
              organisation.
            </p>
            <p className="text-xs text-muted-foreground/80">
              Job Contacts will auto-link to Firm Contacts via Name, ABN, or ACN
              — details of Firm Contacts will auto-populate at a job level
              unless the job has its own overrides.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={allRows.length === 0}
                  className="gap-2"
                >
                  <Download className="h-3.5 w-3.5" />
                  Export
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onClick={() => handleTableExport("current")}
                  disabled={filteredRows.length === 0}
                >
                  Current view
                  <span className="ml-auto text-xs text-muted-foreground">
                    {filteredRows.length}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleTableExport("all")}
                  disabled={allRows.length === 0}
                >
                  All contacts
                  <span className="ml-auto text-xs text-muted-foreground">
                    {allRows.length}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setCustomExportOpen(true)}
                  disabled={allRows.length === 0}
                >
                  Custom export…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <CustomExportDialog
              open={customExportOpen}
              onOpenChange={setCustomExportOpen}
              datasets={buildFirmContactsCustomExportDatasets(allRows)}
              title="Firm contacts"
            />
            {canEdit && <FirmContactImportDialog orgId={orgId} />}
            {canAdd && <NewFirmContactDialog orgId={orgId} />}
          </div>
        </div>
        {!canEdit && <ReadOnlyBanner />}
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-primary/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, ABN, or ACN…"
              className="h-9 pl-9 pr-9 text-sm"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-primary/40 hover:bg-secondary/60 hover:text-primary/70"
              >
                <X className="size-3" />
              </button>
            )}
          </div>

          <FacetedFilter
            title="Category"
            options={categoryFilterOptions}
            selectedValues={Array.from(activeCategories)}
            onSelectionChange={(v) => setActiveCategories(new Set(v))}
            facetCounts={categoryCountsMap}
          />
          <FacetedFilter
            title="Type"
            options={TYPE_FILTER_OPTIONS}
            selectedValues={Array.from(activeTypes)}
            onSelectionChange={(v) =>
              setActiveTypes(new Set(v as ContactType[]))
            }
            facetCounts={typeCountsMap}
          />
          {stateFilterOptions.length > 0 && (
            <FacetedFilter
              title="State"
              options={stateFilterOptions}
              selectedValues={Array.from(activeStates)}
              onSelectionChange={(v) => setActiveStates(new Set(v))}
              facetCounts={stateCountsMap}
            />
          )}
          <Select
            value={groupBy}
            onValueChange={(v) => setGroupBy(v as FirmContactGroupBy)}
          >
            <SelectTrigger className="h-9 w-[170px] text-sm">
              <SelectValue placeholder="Group by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No grouping</SelectItem>
              <SelectItem value="category">Group by category</SelectItem>
              <SelectItem value="type">Group by type</SelectItem>
              <SelectItem value="state">Group by state</SelectItem>
              <SelectItem value="firm">Group by firm</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          {(archivedCount > 0 || showArchived) && (
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              {showArchived
                ? "Hide archived"
                : `Show ${archivedCount} archived`}
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-primary/50">
              {filteredRows.length === allRows.length
                ? `${allRows.length} contact${allRows.length === 1 ? "" : "s"}`
                : `${filteredRows.length} of ${allRows.length}`}
            </span>
            {isFiltered && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                className="h-7 px-2 text-xs text-primary/60"
              >
                Clear all
              </Button>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
          Loading firm contacts…
        </div>
      ) : allRows.length === 0 ? (
        <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/20 text-sm text-muted-foreground">
          <p>No firm contacts yet.</p>
          {canEdit && (
            <p className="text-xs">
              Add banks, regulators, and frequent professionals using the
              &ldquo;New Firm Contact&rdquo; button, or import your existing
              list from a spreadsheet.
            </p>
          )}
        </div>
      ) : (
        <FirmContactsDataTable
          rows={filteredRows}
          groupBy={groupBy}
          customCategories={customCategories}
          selectedFirmContactId={selectedFirmContactId}
          onSelect={(c) => setSelectedFirmContactId(c._id)}
          selectable={canEdit}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelected}
          onSetSelected={setManySelected}
        />
      )}

      {/* Floating bulk actions bar (MOR-915) — mirrors the precedent
          library's pill. Shown while at least one contact is selected. */}
      {canEdit && selectedIds.size > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
          <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-primary-foreground/20 bg-primary px-4 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.18),0_2px_6px_rgba(0,0,0,0.12)]">
            <span className="text-sm font-medium text-primary-foreground">
              {selectedIds.size} selected
            </span>
            <div className="h-4 w-px bg-primary-foreground/20" />
            <Button
              type="button"
              size="sm"
              className="h-8 gap-1.5 bg-white text-xs text-primary hover:bg-white/90"
              onClick={() => setBulkEditOpen(true)}
            >
              <Pencil className="size-3.5" />
              Edit
            </Button>
            <AlertDialog
              open={bulkArchiveOpen}
              onOpenChange={setBulkArchiveOpen}
            >
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-1.5 bg-white text-xs text-red-500 hover:bg-white/90 hover:text-red-600"
                >
                  <Archive className="size-3.5" />
                  Archive
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Archive {selectedIds.size} contact
                    {selectedIds.size !== 1 ? "s" : ""}?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Archived contacts keep their history and existing job links,
                    but new jobs will no longer auto-link to them. You can
                    unarchive them individually later.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={bulkArchiving}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleBulkArchive}
                    disabled={bulkArchiving}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {bulkArchiving && (
                      <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                    )}
                    Archive {selectedIds.size} contact
                    {selectedIds.size !== 1 ? "s" : ""}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
              onClick={clearSelection}
            >
              Done
            </Button>
          </div>
        </div>
      )}

      {canEdit && (
        <BulkEditFirmContactsDialog
          open={bulkEditOpen}
          onOpenChange={setBulkEditOpen}
          orgId={orgId}
          firmContactIds={Array.from(selectedIds)}
          customCategories={customCategories}
          onApplied={clearSelection}
        />
      )}

      <FirmContactDetailPanel
        firmContactId={selectedFirmContactId}
        open={selectedFirmContactId !== null}
        canEdit={canEdit}
        onClose={() => setSelectedFirmContactId(null)}
      />
    </div>
  );
}
